/**
 * AWS Connection — admin (`scope=platform`) + tenant (`scope=tenant`).
 *
 * Same UI for both — the scope is determined by the route path:
 *   /dashboard/admin/aws        → scope=platform (system_admin only)
 *   /dashboard/settings/aws     → scope=tenant   (any tenant admin)
 *
 * Pattern: IAM role assumption with external-id (no access keys ever
 * persisted).  The operator pastes role ARN + region + selected services
 * + external-id, we mark it pending_verification, and the operator can
 * trigger a one-shot verify pass.
 */
import React, { useEffect, useState } from 'react';
import { Cloud, KeyRound, CheckCircle2, Loader2, X, Copy, Check, Activity, Shield, Heart, AlertCircle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function AwsConnection({ scope = 'tenant' }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  // Stable random external-id suggestion — generated once per mount so it
  // doesn't change every render and keeps the lint pure-render rule happy.
  const [suggestedExtId] = useState(() => Math.random().toString(36).slice(2, 18));
  const [healthCheck, setHealthCheck] = useState(null);
  const [runningHealth, setRunningHealth] = useState(false);
  const [sovScore, setSovScore] = useState(null);
  const [form, setForm] = useState({
    role_arn: '', external_id: '', region: 'me-central-1', services_enabled: [], bedrock_model_id: '', notes: '',
  });

  const load = async () => {
    try {
      const { data: d } = await api.get(`/aws/connection/${scope}`);
      setData(d);
      setForm({
        role_arn: d.role_arn || '',
        external_id: '',
        region: d.region || 'me-central-1',
        services_enabled: d.services_enabled || [],
        bedrock_model_id: d.bedrock_model_id || d.default_bedrock_model || '',
        notes: d.notes || '',
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load AWS connection');
    }
  };
  // Initial load + heartbeat. Re-fetch every 20s so the page feels alive
  // and the status pill stays in sync if another admin reconfigures from
  // a different tab.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => { if (!cancelled) await load(); };
    const tickScore = async () => {
      try {
        const { data: s } = await api.get('/aws/sovereignty-score');
        if (!cancelled) setSovScore(s);
      } catch { /* not fatal */ }
    };
    tick(); tickScore();
    const t = setInterval(() => { tick(); tickScore(); }, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [scope]);

  const save = async () => {
    if (!form.role_arn.trim() || !form.external_id.trim()) {
      toast.error('Role ARN and external-id are required.');
      return;
    }
    setBusy(true);
    try {
      await api.put(`/aws/connection/${scope}`, form);
      toast.success('AWS connection saved. Click "Verify" to test it.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data: r } = await api.post(`/aws/connection/${scope}/verify`);
      if (r.ok) {
        toast.success(`Connected. Caller: ${r.caller?.account || 'verified'}`);
      } else {
        toast.error(`Verify failed: ${r.error || 'AssumeRole rejected'}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verify failed');
    } finally { setVerifying(false); }
  };

  const runHealth = async () => {
    setRunningHealth(true);
    setHealthCheck(null);
    try {
      const { data: r } = await api.post(`/aws/connection/${scope}/health`);
      setHealthCheck(r);
      if (r.summary?.failing > 0) {
        toast.error(`${r.summary.failing}/${r.summary.total} services failing`);
      } else if (r.summary?.passing > 0) {
        toast.success(`All ${r.summary.passing} services healthy.`);
      } else {
        toast.message('No services enabled — pick a service first.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Health check failed');
    } finally { setRunningHealth(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect AWS? This will fall back to the local fallback for all services.')) return;
    try { await api.delete(`/aws/connection/${scope}`); toast.success('Disconnected.'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const copyAcct = () => {
    navigator.clipboard.writeText(data?.ve_aws_account_id || '').then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      services_enabled: f.services_enabled.includes(id)
        ? f.services_enabled.filter((s) => s !== id)
        : [...f.services_enabled, id],
    }));
  };

  if (!data) return <div className="pal-card pal-grid-bg p-5"><span className="pal-bracket">[AWS · LOADING…]</span></div>;

  const services = data.services_catalog || [];
  const status = data.status || 'not_configured';

  return (
    <div className="space-y-5" data-testid="aws-connection-page">
      <div className="pal-card pal-card-live pal-grid-bg p-5 flex items-start gap-4">
        <div className="h-12 w-12 rounded-md flex items-center justify-center shrink-0 border"
          style={{ backgroundColor: 'rgba(174,82,234,0.10)', color: 'var(--ve-violet-bright)', borderColor: 'rgba(174,82,234,0.40)' }}>
          <Cloud size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="pal-bracket">[AWS · {scope === 'platform' ? 'PLATFORM' : 'TENANT'} CONNECTION]</div>
          <h1 className="display text-2xl mt-0.5">Keep your data in your account.</h1>
          <p className="text-sm mt-1 max-w-2xl opacity-70">
            Connect your AWS account so {scope === 'tenant' ? 'this tenant' : 'the whole platform'} can store keys, run AI (Bedrock), and write files inside <em>your</em> AWS region. We never store your access keys — we assume an IAM role with an external-id.
          </p>
        </div>
        <span className={`pal-status ${status === 'connected' ? 'pal-status-ok' : status === 'pending_verification' ? 'pal-status-warn' : 'pal-status-violet'} inline-flex items-center gap-1.5`}>
          <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${status === 'connected' ? 'bg-emerald-400' : status === 'pending_verification' ? 'bg-amber-400' : 'bg-[var(--ve-violet-bright)]'}`}>
            <span className={`absolute inset-0 rounded-full opacity-70 ${status === 'connected' ? 'bg-emerald-400' : status === 'pending_verification' ? 'bg-amber-400' : 'bg-[var(--ve-violet-bright)]'} animate-ping`}/>
          </span>
          {String(status).replace('_', ' ').toUpperCase()}
        </span>
      </div>

      {/* Live data-flow strip — luxury Bloomberg-terminal feel */}
      <div className="pal-card pal-grid-bg p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="pal-bracket">[WHERE YOUR DATA LIVES · LIVE VIEW]</div>
          <div className="flex items-center gap-1.5 pal-mono text-[10px] opacity-70">
            <Activity size={11} className="text-[var(--ve-violet-bright)] animate-pulse"/>
            <span>HEARTBEAT · 20s</span>
            {data.verified_at && <span className="opacity-50">· verified {new Date(data.verified_at).toLocaleString()}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-[11px] pal-mono">
          <span className="px-3 py-2 rounded border border-[rgba(174,82,234,0.30)] bg-[rgba(174,82,234,0.06)] flex items-center gap-1.5"><Shield size={11} className="text-[var(--ve-violet-bright)]"/>USER · {scope === 'platform' ? 'system_admin' : 'tenant_admin'}</span>
          <span className="opacity-50">→</span>
          <span className="px-3 py-2 rounded border border-[rgba(174,82,234,0.30)] bg-[rgba(174,82,234,0.06)]">TLS 1.3</span>
          <span className="opacity-50">→</span>
          <span className="px-3 py-2 rounded border border-[rgba(174,82,234,0.30)] bg-[rgba(174,82,234,0.06)]">FastAPI · K8s</span>
          <span className="opacity-50">→</span>
          <span className="px-3 py-2 rounded border border-emerald-400/40 bg-emerald-500/10 text-emerald-300 flex items-center gap-1.5"><Cloud size={11}/>YOUR AWS · {data.region || 'me-central-1'}</span>
          {(data.services_enabled || []).length > 0 && (
            <>
              <span className="opacity-50">↳ routing</span>
              {(data.services_enabled || []).slice(0, 6).map((sid) => (
                <span key={sid} className="px-2 py-1 rounded border border-[rgba(174,82,234,0.30)] bg-[rgba(174,82,234,0.10)] text-[var(--ve-violet-bright)] text-[10px] uppercase tracking-[0.15em]">{sid}</span>
              ))}
            </>
          )}
        </div>
        <div className="mt-3 text-[11px] opacity-65 leading-relaxed">
          When connected, AI prompts, encryption keys, and file storage for {scope === 'tenant' ? 'this tenant' : 'the whole platform'} stay <strong className="text-[var(--ve-violet-bright)]">inside your AWS region</strong> and never traverse the public internet beyond your account.
        </div>
      </div>

      {/* Sovereignty Score — % of LLM calls in last 30d that ran on Bedrock */}
      {sovScore && (
        <div className="pal-card pal-grid-bg p-5" data-testid="sovereignty-score">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="pal-bracket">[SOVEREIGNTY SCORE · LAST {sovScore.window_days}d]</div>
            <div className="pal-mono text-[10px] opacity-55">{sovScore.total_calls} LLM call(s) · generated {new Date(sovScore.generated_at).toLocaleString()}</div>
          </div>
          {sovScore.total_calls === 0 ? (
            <div className="text-[12px] opacity-65 italic">
              No AI Council activity recorded in the last {sovScore.window_days} days. Run a deliberation from <a className="underline" href="/dashboard/ai-council">/dashboard/ai-council</a> to start building your sovereignty trail.
            </div>
          ) : (
            <>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="pal-mono text-5xl font-bold" style={{ color: sovScore.score >= 90 ? '#34D399' : sovScore.score >= 50 ? 'var(--ve-violet-bright)' : '#FB7185' }}>
                  {sovScore.score}<span className="text-2xl opacity-70">%</span>
                </span>
                <span className="text-xs opacity-65 pl-2">INSIDE YOUR AWS · {100 - sovScore.score}% VE GATEWAY FALLBACK</span>
              </div>
              {/* Stacked bar */}
              <div className="mt-4 h-3 w-full rounded-full overflow-hidden bg-[rgba(174,82,234,0.10)] flex">
                <div style={{ width: `${sovScore.score}%`, background: '#34D399' }} title={`${sovScore.score}% on Bedrock`}/>
                <div style={{ width: `${100 - sovScore.score}%`, background: 'var(--ve-violet-bright)' }} title={`${100 - sovScore.score}% on VE Gateway`}/>
              </div>
              <div className="mt-2 flex items-center gap-4 text-[10px] pal-mono opacity-70">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400"/> Bedrock · {(sovScore.by_provider || {}).bedrock || 0}</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[var(--ve-violet-bright)]"/> VE Gateway · {sovScore.total_calls - ((sovScore.by_provider || {}).bedrock || 0)}</span>
              </div>
              {/* Daily sparkline */}
              <div className="mt-4 flex items-end gap-0.5 h-12">
                {(sovScore.daily || []).map((d) => {
                  const h = d.total ? Math.max(4, (d.score / 100) * 48) : 2;
                  return (
                    <div key={d.day} className="flex-1 flex flex-col items-stretch" title={`${d.day} · ${d.bedrock}/${d.total} on Bedrock (${d.score}%)`}>
                      <div className="w-full rounded-t" style={{
                        height: `${h}px`,
                        background: d.total === 0 ? 'rgba(255,255,255,0.06)'
                          : d.score >= 90 ? '#34D399'
                          : d.score >= 50 ? 'var(--ve-violet-bright)'
                          : '#FB7185',
                      }}/>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 text-[10px] opacity-50 pal-mono">
                Bedrock-routed calls keep every prompt + response inside your AWS region — provable to regulators and your board.
              </div>
            </>
          )}
        </div>
      )}

      {/* Trust info */}
      <div className="pal-card pal-grid-bg p-5">
        <div className="pal-bracket mb-3">[STEP 1 · CREATE THE TRUST POLICY IN YOUR AWS]</div>
        <p className="text-sm opacity-75 mb-3">In your AWS account, create an IAM Role with the following trust policy. Use a strong random external-id (e.g. <code className="pal-mono text-xs px-1.5 py-0.5 bg-[rgba(174,82,234,0.10)] rounded">{suggestedExtId}</code>).</p>
        <pre className="pal-mono text-[10px] bg-black/40 text-emerald-300 p-3 rounded border border-[rgba(174,82,234,0.18)] overflow-x-auto">{`{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Principal": { "AWS": "arn:aws:iam::${data.ve_aws_account_id}:root" },
    "Action": "sts:AssumeRole",
    "Condition": { "StringEquals": { "sts:ExternalId": "<your-external-id>" } }
  }]
}`}</pre>
        <button onClick={copyAcct} className="mt-3 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold rounded border pal-mono border-[rgba(174,82,234,0.30)] hover:bg-[rgba(174,82,234,0.10)] flex items-center gap-1.5">
          {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? 'COPIED' : 'COPY · VE AWS ACCOUNT'}
        </button>
      </div>

      {/* Form */}
      <div className="pal-card pal-grid-bg p-5">
        <div className="pal-bracket mb-3">[STEP 2 · PASTE YOUR ROLE DETAILS]</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="pal-mono text-[9px] uppercase tracking-[0.20em] opacity-70 block mb-1">ROLE ARN</label>
            <input value={form.role_arn} onChange={(e) => setForm({ ...form, role_arn: e.target.value })}
              placeholder="arn:aws:iam::123456789012:role/VigilantEntitiesAccess" data-testid="aws-role-arn"
              className="w-full rounded border border-[rgba(174,82,234,0.20)] bg-transparent px-3 py-2 text-sm pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)]" />
          </div>
          <div>
            <label className="pal-mono text-[9px] uppercase tracking-[0.20em] opacity-70 block mb-1">EXTERNAL ID</label>
            <input value={form.external_id} onChange={(e) => setForm({ ...form, external_id: e.target.value })}
              placeholder={data.external_id_masked || 'min 8 chars, random'} data-testid="aws-external-id"
              className="w-full rounded border border-[rgba(174,82,234,0.20)] bg-transparent px-3 py-2 text-sm pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)]" />
            {data.external_id_masked && <div className="text-[9px] mt-1 opacity-50 pal-mono">Current: {data.external_id_masked} (paste blank to keep)</div>}
          </div>
          <div>
            <label className="pal-mono text-[9px] uppercase tracking-[0.20em] opacity-70 block mb-1">REGION</label>
            <input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}
              data-testid="aws-region"
              className="w-full rounded border border-[rgba(174,82,234,0.20)] bg-transparent px-3 py-2 text-sm pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)]" />
          </div>
          <div className="md:col-span-2">
            <label className="pal-mono text-[9px] uppercase tracking-[0.20em] opacity-70 block mb-1">NOTES</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Owner / rotation policy / etc."
              className="w-full rounded border border-[rgba(174,82,234,0.20)] bg-transparent px-3 py-2 text-sm pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)]" />
          </div>
        </div>

        <div className="pal-bracket mt-5 mb-2">[STEP 3 · CHOOSE WHICH SERVICES TO ROUTE THROUGH YOUR AWS]</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
          {services.map((s) => {
            const on = form.services_enabled.includes(s.id);
            return (
              <button key={s.id} type="button" onClick={() => toggleService(s.id)}
                data-testid={`aws-svc-${s.id}`}
                className={`text-left px-3 py-3 rounded border transition-colors ${on ? 'border-[var(--ve-violet)] bg-[rgba(174,82,234,0.10)]' : 'border-[rgba(174,82,234,0.18)] hover:border-[var(--ve-violet)]/55 hover:bg-[rgba(174,82,234,0.04)]'}`}>
                <div className="flex items-center gap-2">
                  {on ? <CheckCircle2 size={13} className="text-[var(--ve-violet-bright)]" /> : <KeyRound size={13} className="opacity-50" />}
                  <span className="pal-mono text-[12px] font-bold">{s.label}</span>
                </div>
                <div className="pal-mono text-[10px] opacity-65 mt-1 leading-relaxed">{s.purpose}</div>
              </button>
            );
          })}
        </div>

        {/* Bedrock model picker — only relevant when bedrock is enabled. */}
        {form.services_enabled.includes('bedrock') && (data.bedrock_models || []).length > 0 && (
          <div className="mt-5" data-testid="aws-bedrock-model-section">
            <div className="pal-bracket mb-2">[STEP 4 · PICK YOUR BEDROCK MODEL]</div>
            <p className="text-[11px] opacity-65 mb-3 leading-relaxed">
              Every AI Council call, Iqra narrative, and Yusuf risk briefing will be invoked against this model inside <strong>{form.region}</strong>. Change anytime — switching is instant and per-tenant.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {(data.bedrock_models || []).map((m) => {
                const on = form.bedrock_model_id === m.id;
                return (
                  <button key={m.id} type="button"
                    onClick={() => setForm({ ...form, bedrock_model_id: m.id })}
                    data-testid={`aws-bedrock-model-${m.id.replace(/[.:]/g, '_')}`}
                    className={`text-left px-3 py-3 rounded border transition-colors ${on ? 'border-emerald-400/60 bg-emerald-500/10' : 'border-[rgba(174,82,234,0.18)] hover:border-[rgba(174,82,234,0.50)] hover:bg-[rgba(174,82,234,0.04)]'}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="pal-mono text-[12px] font-bold">{m.label}</span>
                      <span className={`pal-mono text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded ${on ? 'bg-emerald-500/20 text-emerald-300' : 'bg-[rgba(174,82,234,0.10)] text-[var(--ve-violet-bright)]'}`}>
                        {m.family}{m.is_default ? ' · default' : ''}
                      </span>
                    </div>
                    <div className="pal-mono text-[10px] opacity-65 leading-relaxed">{m.notes}</div>
                    <div className="pal-mono text-[9px] opacity-40 mt-1 break-all">{m.id}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Per-feature Bedrock model overrides — only when bedrock enabled
           AND the connection is live, otherwise the overrides have no
           effect.  Lets operators put Council on Sonnet, Iqra on Haiku,
           Founder Vetting on Mistral, etc. */}
        {form.services_enabled.includes('bedrock') && (data.feature_catalog || []).length > 0 && (
          <FeatureModelOverrides
            scope={scope}
            featureCatalog={data.feature_catalog || []}
            bedrockModels={data.bedrock_models || []}
            defaultModelId={form.bedrock_model_id || data.default_bedrock_model}
            initialMap={data.feature_models || {}}
          />
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          <button onClick={save} disabled={busy} data-testid="aws-save"
            className="px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold rounded pal-mono bg-[var(--ve-violet)] text-black hover:bg-[var(--ve-violet-bright)] disabled:opacity-50 flex items-center gap-1.5">
            {busy ? <Loader2 size={11} className="animate-spin" /> : null} SAVE CONNECTION
          </button>
          <button onClick={verify} disabled={verifying || status === 'not_configured'} data-testid="aws-verify"
            className="px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold rounded pal-mono border border-[rgba(174,82,234,0.40)] text-[var(--ve-violet-bright)] hover:bg-[rgba(174,82,234,0.10)] disabled:opacity-40 flex items-center gap-1.5">
            {verifying ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />} VERIFY NOW
          </button>
          <button onClick={runHealth} disabled={runningHealth || status !== 'connected' || (form.services_enabled || []).length === 0} data-testid="aws-health-check"
            className="px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold rounded pal-mono border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40 flex items-center gap-1.5">
            {runningHealth ? <Loader2 size={11} className="animate-spin" /> : <Heart size={11} />} RUN HEALTH CHECK
          </button>
          {status !== 'not_configured' && (
            <button onClick={disconnect} data-testid="aws-disconnect"
              className="ml-auto px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold rounded pal-mono border border-rose-400/30 text-rose-400 hover:bg-rose-500/10 flex items-center gap-1.5">
              <X size={11} /> DISCONNECT
            </button>
          )}
        </div>
      </div>

      {/* ─── Bloomberg-style health-check grid ─── */}
      {healthCheck && (
        <div className="pal-card pal-grid-bg p-5" data-testid="aws-health-grid">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="pal-bracket">[LIVE HEALTH CHECK · {healthCheck.summary.passing}/{healthCheck.summary.total} PASSING]</div>
            <div className="pal-mono text-[10px] opacity-60">checked {new Date(healthCheck.summary.checked_at).toLocaleString()}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {healthCheck.grid.map((g) => (
              <div key={g.service} data-testid={`aws-health-${g.service}`}
                className={`p-3 rounded border ${g.ok ? 'border-emerald-400/40 bg-emerald-500/5' : 'border-rose-400/40 bg-rose-500/5'}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className={`pal-mono text-[12px] font-bold uppercase ${g.ok ? 'text-emerald-300' : 'text-rose-300'}`}>{g.service}</span>
                  <span className={`inline-flex items-center gap-1 pal-mono text-[10px] uppercase tracking-[0.15em] ${g.ok ? 'text-emerald-300' : 'text-rose-300'}`}>
                    {g.ok ? <><CheckCircle2 size={10}/> OK</> : <><AlertCircle size={10}/> FAIL</>}
                    {typeof g.latency_ms === 'number' && <span className="opacity-60">· {g.latency_ms}ms</span>}
                  </span>
                </div>
                {g.ok ? (
                  <div className="pal-mono text-[10px] opacity-75 leading-relaxed">{g.detail}</div>
                ) : (
                  <div className="pal-mono text-[10px] text-rose-300/80 leading-relaxed break-words">{g.error}</div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-3 pal-mono text-[10px] opacity-55 leading-relaxed">
            Each probe is a read-only API call inside your AWS region using the assumed IAM role. Nothing about your data leaves your account during this check.
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  FeatureModelOverrides — small grid letting operators pick a different     */
/*  Bedrock model per workload (Council / Iqra / Founder Vetting / ...).      */
/*  Falls back to the connection's default `bedrock_model_id` when a feature  */
/*  isn't overridden.                                                         */
/* -------------------------------------------------------------------------- */
function FeatureModelOverrides({ scope, featureCatalog, bedrockModels, defaultModelId, initialMap }) {
  const [map, setMap] = useState(initialMap || {});
  const [saving, setSaving] = useState(false);

  const saveOne = async (featureId, modelId) => {
    setSaving(true);
    try {
      const next = { ...map, [featureId]: modelId || null };
      const { data } = await api.patch(`/aws/connection/${scope}/feature-models`, {
        feature_models: { [featureId]: modelId || null },
      });
      queueMicrotask(() => setMap(data.feature_models || next));
      toast.success(modelId ? 'Override saved.' : 'Override cleared — using default.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save override.');
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-5" data-testid="aws-feature-models-section">
      <div className="pal-bracket mb-2">[STEP 5 · PER-FEATURE MODEL OVERRIDES (OPTIONAL)]</div>
      <p className="text-[11px] opacity-65 mb-3 leading-relaxed">
        Pick a <strong>different</strong> Bedrock model per workload. Cheap Haiku for nightly Iqra digests, premium Sonnet for AI Council debates, EU-residency Mistral for Founder Vetting. Anything you don&apos;t override falls back to the default chosen in Step 4.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {featureCatalog.map((f) => {
          const current = map[f.id] || '';
          const effective = current || defaultModelId;
          const effectiveLabel = (bedrockModels.find((m) => m.id === effective) || {}).label || effective || '—';
          return (
            <div key={f.id} data-testid={`feature-row-${f.id}`}
              className="p-3 rounded border border-[rgba(174,82,234,0.18)]">
              <div className="flex items-baseline justify-between gap-2">
                <span className="pal-mono text-[12px] font-bold">{f.label}</span>
                <span className="pal-mono text-[9px] opacity-50 uppercase tracking-[0.18em]">{current ? 'override' : 'default'}</span>
              </div>
              <div className="pal-mono text-[10px] opacity-65 mt-1 leading-relaxed">{f.desc}</div>
              <div className="mt-2 flex items-center gap-2">
                <select
                  value={current}
                  onChange={(e) => saveOne(f.id, e.target.value)}
                  disabled={saving}
                  data-testid={`feature-select-${f.id}`}
                  className="flex-1 rounded border border-[rgba(174,82,234,0.25)] bg-transparent px-2 py-1.5 text-[11px] pal-mono focus:outline-none focus:border-[var(--ve-violet-bright)] disabled:opacity-50"
                >
                  <option value="">— use default ({effectiveLabel}) —</option>
                  {bedrockModels.map((m) => (
                    <option key={m.id} value={m.id}>{m.label} · {m.family}</option>
                  ))}
                </select>
                {current && (
                  <button
                    type="button"
                    onClick={() => saveOne(f.id, '')}
                    data-testid={`feature-clear-${f.id}`}
                    className="px-2 py-1.5 rounded border border-rose-400/30 text-rose-400 text-[9px] uppercase tracking-[0.18em] hover:bg-rose-500/10"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

